import { NgTemplateOutlet } from '@angular/common';
import {
  injectFloatingLayout,
  injectFloatingPageClasses,
  provideSystemTabsLayout,
} from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { ChevronDownIconComponent, EditIconComponent } from '@kuiper/icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { PositionService } from '../position.service';
import { PositionDetail } from '../position.model';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import {
  FEATURE_FLAGS,
  FeatureFlagsService,
} from '../../../../employee-flow/my-inbox/contributed-tasks/contributed-tasks.imports';
import { BadgeComponent } from 'all-libraries/ibusiness-core/src/ui-components';
import { UserStatusPipe } from '../../../../../common/pipes/user-status/user-status.pipe';

@Component({
  selector: 'lib-position-details',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    TranslocoPipe,
    OverlayLoaderComponent,
    RouterLink,
    TenantPathPipe,
    ChevronDownIconComponent,
    EditIconComponent,
    BadgeComponent,
    UserStatusPipe,
  ],
  providers: [provideSystemTabsLayout()],
  templateUrl: './position-details.component.html',
  styleUrl: './position-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PositionDetailsComponent implements OnInit {
  readonly ADMIN_ROUTES = ADMIN_ROUTES;
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();

  private readonly positionService = inject(PositionService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly tenantService = inject(TenantService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlagService = inject(FeatureFlagsService);

  readonly isPositionStatuesEnhancementEnabled = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT.position_statues_enhancement
    )
  ).asReadonly();

  positionDetails = signal<PositionDetail | null>(null);
  isLoading = signal<boolean>(false);

  positionId = '';

  ngOnInit(): void {
    this.positionId = this.route.snapshot.params['id'] ?? '';
    this.getPositionDetails();
  }

  getPositionDetails(): void {
    this.isLoading.set(true);
    this.positionService
      .getPositionDetails(this.positionId)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: res => {
          this.positionDetails.set(res.data);
        },
      });
  }

  goBack(): void {
    this.router.navigate([
      this.tenantService.adminPath(ADMIN_ROUTES.POSITIONS.LIST),
    ]);
  }

  navigateToEdit(): void {
    this.router.navigate([
      this.tenantService.adminPath(
        `${ADMIN_ROUTES.POSITIONS.FORM}/${this.positionId}`
      ),
    ]);
  }
}
